import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';
import { CustomTrackingEvent, TrackingEventData } from './tracking.model';

/**
 * Listed below are valid Reddit Pixel Tracking events.
 * Note that Reddit does not allow custom tracking events
 *
 * More info here: https://advertising.reddithelp.com/en/categories/managing-ads/implementing-reddit-pixel
 */
export enum RedditPixelTrackingEventName {
  PAGE_VISIT = 'PageVisit',
  VIEW_CONTENT = 'ViewContent',
  SEARCH = 'Search',
  ADD_TO_CART = 'AddToCart',
  ADD_TO_WISHLIST = 'AddToWishlist',
  PURCHASE = 'Purchase',
  LEAD = 'Lead',
  SIGN_UP = 'SignUp',
}

@Injectable()
export class RedditPixelTracking {
  private isEnabled = false;

  constructor(
    private ngZone: NgZone,
    @Inject(TRACKING_CONFIG) private config: TrackingConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    if (this.config.redditPixelId && isPlatformBrowser(this.platformId)) {
      this.isEnabled = true;
    }
  }

  trackPageView(): void {
    if (!this.isEnabled) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.loadTrackingSnippet();
      window.rdt('track', RedditPixelTrackingEventName.PAGE_VISIT);
    });
  }

  trackCustomEvent(payload: CustomTrackingEvent & TrackingEventData): void {
    if (!this.isEnabled) {
      return;
    }

    if (payload.redditEventName) {
      this.ngZone.runOutsideAngular(() => {
        this.loadTrackingSnippet();

        window.rdt('track', payload.redditEventName);
      });
    }
  }

  private loadTrackingSnippet(): void {
    if (window.rdt) {
      return;
    }

    window.rdt = function rdt() {
      if (window.rdt.sendEvent) {
        // eslint-disable-next-line prefer-rest-params, prefer-spread
        window.rdt.sendEvent.apply(window.rdt, arguments);
      } else {
        // eslint-disable-next-line prefer-rest-params
        window.rdt.callQueue.push(arguments);
      }
    };

    window.rdt.callQueue = [];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://www.redditstatic.com/ads/pixel.js';
    const s = document.getElementsByTagName('script')[0];
    (s.parentNode as Node).insertBefore(t, s);
    window.rdt('init', this.config.redditPixelId);
  }
}
